/**
 * Client-side capability mapping for providers.
 * No API call needed — capabilities are known at build time.
 */
import type { ProviderCapability } from '@/lib/control-types';

const PROVIDER_CAPABILITIES: Record<string, ProviderCapability[]> = {
  solark: ['read_telemetry', 'read_settings', 'write_settings', 'historical'],
  eg4: ['read_telemetry', 'read_settings', 'write_settings'],
  franklinwh: ['read_telemetry', 'read_settings', 'write_settings', 'historical'],
  enphase: ['read_telemetry'],
  tesla: ['read_telemetry', 'read_settings', 'historical'],
  victron: ['read_telemetry'],
  solaredge: ['read_telemetry'],
  solis: ['read_telemetry', 'read_settings', 'write_settings', 'historical'],
};

export function getProviderCapabilities(providerType: string): ProviderCapability[] {
  return PROVIDER_CAPABILITIES[providerType] ?? ['read_telemetry'];
}

export function providerCanRead(providerType: string): boolean {
  return getProviderCapabilities(providerType).includes('read_settings');
}

export function providerCanWrite(providerType: string): boolean {
  return getProviderCapabilities(providerType).includes('write_settings');
}

/**
 * Hook: returns capabilities for the given provider.
 */
export function useSystemCapabilities(providerType: string | null | undefined) {
  const caps = providerType ? getProviderCapabilities(providerType) : ['read_telemetry'];
  return {
    capabilities: caps as ProviderCapability[],
    canReadSettings: caps.includes('read_settings'),
    canWriteSettings: caps.includes('write_settings'),
    hasHistorical: caps.includes('historical'),
  };
}
